import React, { useState, useEffect } from 'react';

const FilterSidebar = ({ onFiltersChange, categories, brands }) => {
  const [filters, setFilters] = useState({
    category: 'all',
    brand: [],
    minPrice: '',
    maxPrice: '',
    inStock: false,
    minRating: '',
    search: ''
  });

  const [isCollapsed, setIsCollapsed] = useState(false);

  // Update parent component when filters change
  useEffect(() => {
    const activeFilters = {
      ...filters,
      minPrice: filters.minPrice ? parseFloat(filters.minPrice) : undefined,
      maxPrice: filters.maxPrice ? parseFloat(filters.maxPrice) : undefined,
      minRating: filters.minRating ? parseFloat(filters.minRating) : undefined
    };
    onFiltersChange(activeFilters);
  }, [filters, onFiltersChange]);

  const handleCategoryChange = (category) => {
    setFilters(prev => ({ ...prev, category }));
  };

  const handleBrandChange = (brand) => {
    setFilters(prev => ({
      ...prev,
      brand: prev.brand.includes(brand)
        ? prev.brand.filter(b => b !== brand)
        : [...prev.brand, brand]
    }));
  };

  const handlePriceChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleRatingChange = (rating) => {
    setFilters(prev => ({ 
      ...prev, 
      minRating: prev.minRating === rating ? '' : rating 
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      category: 'all',
      brand: [],
      minPrice: '',
      maxPrice: '',
      inStock: false,
      minRating: '',
      search: ''
    });
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <svg
        key={index}
        className={`w-4 h-4 ${index < rating ? 'text-yellow-400' : 'text-gray-300'} fill-current`}
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ));
  };

  return (
    <div className="bg-white rounded-lg shadow-md">
      {/* Mobile toggle button */}
      <div className="lg:hidden flex justify-between items-center p-4 border-b">
        <h2 className="text-lg font-semibold text-gray-800">Filters</h2>
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="text-gray-600 hover:text-gray-800"
        >
          <svg className={`w-5 h-5 transform ${isCollapsed ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      <div className={`p-4 space-y-6 ${isCollapsed ? 'hidden lg:block' : ''}`}>
        {/* Header with Clear All */}
        <div className="hidden lg:flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-800">Filters</h2>
          <button
            onClick={clearAllFilters}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Clear All
          </button>
        </div>

        {/* Search */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Search Products
          </label>
          <input
            type="text"
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            placeholder="Search by name, brand..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Categories */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3">Category</h3>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="radio"
                name="category"
                value="all"
                checked={filters.category === 'all'}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="mr-2 text-blue-600"
              />
              <span className="text-sm text-gray-600">All Categories</span>
            </label>
            {categories.map(category => (
              <label key={category} className="flex items-center">
                <input
                  type="radio"
                  name="category"
                  value={category}
                  checked={filters.category === category}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="mr-2 text-blue-600"
                />
                <span className="text-sm text-gray-600">{category}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Brands */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3">Brand</h3>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {brands.map(brand => (
              <label key={brand} className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.brand.includes(brand)}
                  onChange={() => handleBrandChange(brand)}
                  className="mr-2 text-blue-600"
                />
                <span className="text-sm text-gray-600">{brand}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Price Range */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3">Price Range</h3>
          <div className="flex space-x-2">
            <input
              type="number"
              placeholder="Min"
              value={filters.minPrice}
              onChange={(e) => handlePriceChange('minPrice', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="number"
              placeholder="Max"
              value={filters.maxPrice}
              onChange={(e) => handlePriceChange('maxPrice', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Rating */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3">Minimum Rating</h3>
          <div className="space-y-2">
            {[4, 3, 2, 1].map(rating => (
              <label key={rating} className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="rating"
                  checked={filters.minRating === rating}
                  onChange={() => handleRatingChange(rating)}
                  className="sr-only"
                />
                <div className="flex items-center space-x-1">
                  {renderStars(rating)}
                  <span className="text-sm text-gray-600 ml-2">& up</span>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* In Stock */}
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={filters.inStock}
              onChange={(e) => setFilters(prev => ({ ...prev, inStock: e.target.checked }))}
              className="mr-2 text-blue-600"
            />
            <span className="text-sm text-gray-700">In Stock Only</span>
          </label>
        </div>

        {/* Mobile Clear All Button */}
        <div className="lg:hidden">
          <button
            onClick={clearAllFilters}
            className="w-full py-2 px-4 text-sm text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50"
          >
            Clear All Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterSidebar;
